import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ShadeToolsService} from '../../../../../core/services/shade-tools.service';
import {UrlService} from '../../../../../core/services/url.service';
import {Tile, TILE_TYPE_SUPER_FOLDER, TILE_TYPE_SUPER_CANVAS, TileId} from '../../../../../shared/models/tile';
import {EMPTY_ID} from '../../../../../shared/constants/empty-id';
import {TilesService} from '../../../../../core/services/tiles.service';
import {SessionService} from '../../../../../core/services/session.service';
import {SubtitleItem} from '../../../../../core/state/title.state';
import {TitleChangedAction} from '../../../../../core/actions/title.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../app/app.state';
import {TileEditorComponent} from '../../../../components/tile-editor/tile-editor.component';
import {SpinnerComponent} from '../../../../components/spinner/spinner.component';
import {FaIconComponent} from '../../../../components/fa-icon/fa-icon.component';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';
import {BulkModalService} from '@features/tile-bulk-operations/shared/services/bulk-modal.service';
import {FeatureFlagsService, FeatureFlag} from '../../../../../core/services/feature-flags.service';
import {NumericTileCreatorComponent} from '@features/formula-tile/creator/components/numeric-tile-creator/numeric-tile-creator.component';

@Component({
  selector: 'motn-tiles-new',
  templateUrl: './tiles-new.component.html',
  styleUrls: ['./tiles-new.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TileEditorComponent,
    SpinnerComponent,
    FaIconComponent,
    NumericTileCreatorComponent,
  ],
})
export class TilesNewComponent implements OnInit {
  creatingTileFolder: boolean = false;
  creatingCanvasTile: boolean = false;
  creatingFormulaTile: boolean = false;
  editingTile: TileId = EMPTY_ID;

  get showBulkCreation(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_MODBUS_BULK_CREATION) ||
           this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_BACNET_BULK_CREATION) ||
           this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_LORAWAN_BULK_CREATION);
  }

  get showCanvasTileCreation(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_CANVAS_TILE_CREATION);
  }

  get showFormulaTileCreation(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_FORMULA_TILE_CREATION);
  }

  createNumericTile(){
    this.creatingFormulaTile = true;
  }

  constructor(
    private tilesService: TilesService,
    private shadeTools: ShadeToolsService,
    private session: SessionService,
    private store: Store<AppState>,
    private bulkModalService: BulkModalService,
    private featureFlagsService: FeatureFlagsService,
  ) {
  }


  ngOnInit(): void {
    this.updateTitle();
  }


  protected updateTitle() {
    const subtitle: SubtitleItem[] = [{
      text: 'Ready to add more',
    }];

    this.store.dispatch(new TitleChangedAction(
      '',
      'Tiles',
      subtitle,
    ));
  }


  showPointBasedTilesInfo() {
    this.shadeTools.startDialog(
      `Create a Point-Based Tile`,
      `Toggle and Multi-Choice point-based tiles are created by:\n1. Navigating to the Devices view\n2. Locating the device where the point lives\n3. Clicking on "Discover Points" or "Configure Points" (depending on the protocol)`,
      [
        {
          text: 'Go to Devices',
          callback: () => {
          },
          routerLink: UrlService.controlsUrls.devicesExisting,
          color: DialogBoxButtonColor.Green,
        },
        {
          text: 'Close',
          callback: () => {
          },
          color: DialogBoxButtonColor.Alt,
        },
      ],
    );
  }


  createTileFolder() {
    this.creatingTileFolder = true;

    const tile: Tile = Tile.create({
      Enabled: true,
      Name: 'New Folder',
      Owner: this.session.userId,
      Structure: {
        Protocol: null,
        Priority: null,
        TileIDs: [],
      },
      Appearance: {
        Color: '',
        Icon: 'folder',
      },
      Type: TILE_TYPE_SUPER_FOLDER,
    });

    this.tilesService.createTile$(tile).subscribe(
      (tileId) => {
        this.editingTile = tileId;
        this.creatingTileFolder = false;
      },
      () => {
        this.creatingTileFolder = false;
      },
    );
  }


  closeTileEditor() {
    this.editingTile = EMPTY_ID;
  }


  createCanvasTile() {
    this.creatingCanvasTile = true;

    const tile: Tile = Tile.create({
      Enabled: true,
      Name: 'New Canvas',
      Owner: this.session.userId,
      Structure: {
        Priority: 8,
        Image: null,
        Points: [],
        Cluster: {
          Enabled: this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_CANVAS_CLUSTERING_BY_DEFAULT),
        },
      },
      Appearance: {
        Color: 'black-white',
        Icon: 'image',
      },
      Type: TILE_TYPE_SUPER_CANVAS,
    });

    this.tilesService.createTile$(tile).subscribe(
      (tileId) => {
        this.editingTile = tileId;
        this.creatingCanvasTile = false;
      },
      () => {
        this.creatingCanvasTile = false;
      },
    );
  }


  openBulkCreationModal() {
    this.bulkModalService.openCreationModal({
      // Open without a specific device ID since we're importing from CSV
    });
  }
}
